import Form from 'antd/lib/form';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '@/typescriptHelpers';
import Checkbox from 'antd/lib/checkbox';
import { AppSettingsUpdatable } from '@/actions/updatable';
import { UpdateFixesFeatureSettingsRequest, tryUpdateAppSettings } from '@/actions/appSettings';
import { selectFixRequestFormFieldsSettings } from '@/selectors/selectors';
import { FixRequestFormFieldSettings } from '@/types';
import { Button, InputNumber } from 'antd/lib';
import { useWatch } from 'antd/es/form/Form';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import { useFixSettingsModal } from './useFixSettingsModal';
import FixSettingsDrawer from './FixSettingsDrawer';

interface EditFixFormTemplateModalProps extends AppComponentProps {
    open: boolean;
    onCancel: () => void;
}

const EditFixFormTemplateModal: React.FC<EditFixFormTemplateModalProps> = (props: EditFixFormTemplateModalProps) => {
    const { form, isLoading, afterClose } = useFixSettingsModal({ open: props.open, onCancel: props.onCancel });
    const dispatch = useDispatch();

    const fixRequestFormFieldsSettings = useSelector(selectFixRequestFormFieldsSettings(props.appId));

    const watchedFormFields = useWatch('customFields', form);

    const initialValues = {
        customFields: fixRequestFormFieldsSettings,
    };

    const onFinish = (value) => {
        const request: UpdateFixesFeatureSettingsRequest = {
            appId: props.appId,
            orgId: props.orgId,
            // No fieldId is stored on settings — it is derived from the field name where needed.
            fixRequestFormFieldsSettings: value.customFields ?? [],
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.RequiresFixRequestsApprovals, request));
    };

    return (
        <FixSettingsDrawer
            title="Fix request form template"
            open={props.open}
            loading={isLoading}
            onSave={form.submit}
            onCancel={props.onCancel}
            afterClose={afterClose}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text style={{ color: antGrey.grey7 }}>
                    Define required information that users must provide when submitting a fix request.
                </Text>
                <Form
                    onFinish={onFinish}
                    initialValues={initialValues}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item label="Fix request form fields">
                        <Form.List
                            name="customFields"
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        // Zero form fields is allowed. When fields exist, their names must be unique
                                        // (names become the field ids, so duplicates would collide).
                                        const fields = (value ?? []) as FixRequestFormFieldSettings[];
                                        const seen = new Set<string>();
                                        for (const field of fields) {
                                            const name = field?.name?.trim().toLowerCase();
                                            if (!name) continue;
                                            if (seen.has(name)) {
                                                return Promise.reject(new Error('Form field names must be unique'));
                                            }
                                            seen.add(name);
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                                    {fields.map(({ key, name: fieldIndex, ...restField }) => (
                                        <div
                                            key={key}
                                            style={{
                                                border: `1px solid ${antGrey.grey4}`,
                                                borderRadius: 8,
                                                padding: 12,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                backgroundColor: runwayColors.white,
                                                position: 'relative',
                                            }}
                                        >
                                            <Button
                                                type="text"
                                                icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                onClick={() => remove(fieldIndex)}
                                                style={{ position: 'absolute', top: 6, right: 6 }}
                                            />
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'name']}
                                                label="Field name"
                                                style={{ marginBottom: 0, paddingRight: 32 }}
                                                rules={[{ required: true, message: 'Field name is required' }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'required']}
                                                valuePropName="checked"
                                                getValueFromEvent={(e) => e.target.checked}
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Checkbox>Input required</Checkbox>
                                            </Form.Item>
                                            {watchedFormFields?.[fieldIndex]?.required && (
                                                <Form.Item
                                                    {...restField}
                                                    name={[fieldIndex, 'minCharacters']}
                                                    label="Minimum characters"
                                                    style={{ marginBottom: 0, marginTop: 6, marginLeft: 24 }}
                                                >
                                                    <InputNumber min={1} placeholder="None" />
                                                </Form.Item>
                                            )}
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'description']}
                                                label="Description"
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <MarkdownEditor
                                                    preview="edit"
                                                    textareaProps={{
                                                        placeholder:
                                                            'Optional helper shown with the field. Markdown is supported.',
                                                    }}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'placeholder']}
                                                label="Placeholder text"
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Input.TextArea rows={2} placeholder="Optional placeholder text" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[fieldIndex, 'prePopulatedText']}
                                                label="Pre-populated text"
                                                style={{ marginBottom: 0, marginTop: 10 }}
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="Optional pre-populated text (e.g. for a template)"
                                                />
                                            </Form.Item>
                                        </div>
                                    ))}
                                    <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        onClick={() =>
                                            add({
                                                name: '',
                                                placeholder: '',
                                                description: '',
                                                prePopulatedText: '',
                                                required: false,
                                            } satisfies FixRequestFormFieldSettings)
                                        }
                                    >
                                        Add field
                                    </Button>
                                    <Form.ErrorList errors={errors} />
                                </div>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </div>
        </FixSettingsDrawer>
    );
};

export default EditFixFormTemplateModal;
